import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ModernSettingsLayout } from '@/components/settings/ModernSettingsLayout';
import { ModernAccountTab } from '@/components/settings/tabs/ModernAccountTab';
import { ModernSecurityTab } from '@/components/settings/tabs/ModernSecurityTab';
import { ModernCreditsTab } from '@/components/settings/tabs/ModernCreditsTab';
import { NotificationsTab } from '@/components/settings/tabs/NotificationsTab';
import { AppearanceTab } from '@/components/settings/tabs/AppearanceTab';
import { ApiSettingsTab, ApiKey } from '@/components/settings/tabs/ApiSettingsTab';
import { AboutTab } from '@/components/settings/tabs/AboutTab';
import LLMControlsTab from '@/components/settings/tabs/LLMControlsTab';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';
import { BloombergRadioButton } from '@/components/trading/BloombergRadioButton';
import { DarkNavigation } from '@/components/layout/DarkNavigation';
import { DarkSidebar } from '@/components/layout/DarkSidebar';

// Admin email list - in production, this should be configured via environment variables
const ADMIN_EMAILS = [
  'es@papartners.co',
  'team@stewietrader.com',
  'support@stewietrader.com'
];

const ModernSettings = () => {
  const location = useLocation();
  const locationState = location.state as { tab?: string } || {};
  const [activeTab, setActiveTab] = useState(locationState.tab || 'account');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Navigation state for dark theme integration
  const [activeView, setActiveView] = useState('trader');
  const [showRoles, setShowRoles] = useState(false);

  // User information state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          const userEmail = user.email.toLowerCase();
          const userIsAdmin = ADMIN_EMAILS.includes(userEmail);
          setIsAdmin(userIsAdmin);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, []);

  // Appearance settings state
  const [darkMode, setDarkMode] = useState(false);
  const [compactLayout, setCompactLayout] = useState(false);

  // Security settings state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Notification settings state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [tradingAlerts, setTradingAlerts] = useState(true);
  const [marketUpdates, setMarketUpdates] = useState(false);
  const [securityAlerts, setSecurityAlerts] = useState(true);
  const [productUpdates, setProductUpdates] = useState(false);
  const [newFeatures, setNewFeatures] = useState(true);

  // API keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);

  // Fetch initial settings data
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log('ModernSettings: Fetching user session...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('ModernSettings: Session error:', sessionError);
          throw sessionError;
        }
        
        if (!session?.user) {
          console.warn('ModernSettings: No session found, but ProtectedRoute should handle this');
          // Don't throw error here - let ProtectedRoute handle authentication
          // This component should only render if user is authenticated
          setEmail('');
          setFullName('');
          setIsLoading(false);
          return;
        }

        console.log('ModernSettings: User session found:', session.user.email);
        setEmail(session.user.email || '');

        // Fetch profile data
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileError) {
          console.error('ModernSettings: Profile fetch error:', profileError);
          // Don't throw error for profile fetch - just log and continue
          console.warn('ModernSettings: Could not fetch profile, continuing with empty name');
          setFullName('');
        } else {
          setFullName(profileData?.full_name || '');
        }

        // Simulate loading settings
        await new Promise(resolve => setTimeout(resolve, 300));

        // Load API keys from environment variables
        setApiKeys([
          { id: 'alpha-vantage', name: 'Alpha Vantage API Key', value: import.meta.env.VITE_ALPHA_VANTAGE_API_KEY || '', isVisible: false },
          { id: 'alpaca-live', name: 'Alpaca Live API Key', value: import.meta.env.VITE_ALPACA_API_KEY || '', isVisible: false },
          { id: 'alpaca-paper', name: 'Alpaca Paper API Key', value: import.meta.env.VITE_ALPACA_PAPER_API_KEY || '', isVisible: false },
        ]);

        console.log('ModernSettings: Settings loaded successfully');
      } catch (error: any) {
        console.error('ModernSettings: Error loading settings:', error);
        setError(error.message || "Failed to load settings.");
        toast({ 
          title: "Error", 
          description: error.message || "Failed to load settings", 
          variant: "destructive" 
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [toast]);

  const toggleApiKeyVisibility = (index: number) => {
    setApiKeys(apiKeys.map((key, i) =>
      i === index ? { ...key, isVisible: !key.isVisible } : key
    ));
  };

  // Render loading state
  const renderLoadingSkeleton = () => (
    <div className="p-6 space-y-6">
      <Skeleton className="h-8 w-1/3 mb-4" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-1/4" />
    </div>
  );

  // Render the active tab content
  const renderTabContent = () => {
    if (isLoading) return renderLoadingSkeleton();
    if (error) return <div className="text-red-500 p-6">Error: {error}</div>;

    const variants = {
      hidden: { opacity: 0, x: 10 },
      visible: { opacity: 1, x: 0, transition: { duration: 0.3 } }
    };

    const content = (() => {
      switch (activeTab) {
        case 'account':
          return (
            <ModernAccountTab
              email={email}
              fullName={fullName}
              setFullName={setFullName}
            />
          );
        case 'security':
          return (
            <ModernSecurityTab
              twoFactorEnabled={twoFactorEnabled}
              setTwoFactorEnabled={setTwoFactorEnabled}
            />
          );
        case 'notifications':
          return (
            <NotificationsTab
              emailNotifications={emailNotifications}
              setEmailNotifications={setEmailNotifications}
              tradingAlerts={tradingAlerts}
              setTradingAlerts={setTradingAlerts}
              marketUpdates={marketUpdates}
              setMarketUpdates={setMarketUpdates}
              securityAlerts={securityAlerts}
              setSecurityAlerts={setSecurityAlerts}
              productUpdates={productUpdates}
              setProductUpdates={setProductUpdates}
              newFeatures={newFeatures}
              setNewFeatures={setNewFeatures}
            />
          );
        case 'appearance':
          return (
            <AppearanceTab
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              compactLayout={compactLayout}
              setCompactLayout={setCompactLayout}
            />
          );
        case 'api':
          return (
            <ApiSettingsTab
              apiKeys={apiKeys}
              toggleApiKeyVisibility={toggleApiKeyVisibility}
            />
          );
        case 'credits':
          return <ModernCreditsTab />;
        case 'llm-controls':
          return isAdmin ? <LLMControlsTab /> : null;
        case 'about':
          return <AboutTab />;
        default:
          return (
            <ModernAccountTab
              email={email}
              fullName={fullName}
              setFullName={setFullName}
            />
          );
      }
    })();

    return (
      <motion.div
        key={activeTab}
        initial="hidden"
        animate="visible"
        variants={variants}
      >
        {content}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Dark Navigation Header */}
      <DarkNavigation
        activeView={activeView}
        setActiveView={setActiveView}
        showRoles={showRoles}
        setShowRoles={setShowRoles}
      />
      
      <div className="flex flex-1">
        {/* Dark Sidebar */}
        <DarkSidebar className="hidden md:block" />
        
        {/* Main Settings Content */}
        <div className="flex-1 overflow-hidden">
          <ModernSettingsLayout
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isAdmin={isAdmin}
          >
            {renderTabContent()}
          </ModernSettingsLayout>
        </div>
      </div>
      
      {/* Bloomberg Radio Button with fixed positioning */}
      <div className="fixed bottom-4 right-4 z-50">
        <BloombergRadioButton />
      </div>
    </div>
  );
};

export default ModernSettings;